/**
 * Live Readings promo modal (HORO-10471) per Figma 4481:14168 (mobile
 * bottom-sheet) / 4480:12785 (desktop centered).
 *
 * Announces the fake-door Live Readings waitlist and routes into it. The
 * framing is deliberate: the brainstorm frame rejected a persistent marketplace
 * banner ("reads as an ad break, fragments the companion into a marketplace")
 * in favour of Veda introducing something. Keep it a referral, not an ad.
 *
 * Frequency, per the designer annotations on the Figma:
 *   "this modal will only be shown to the registered user once"
 *   "If they click out of it - they wont see the modal again"
 *
 * So it fires ONCE PER USER, EVER. Dismissing (X, backdrop, Esc) or joining
 * both suppress it permanently. Suppression is stored server-side on the user
 * document, which is why this is shown to signed-in users only — an anonymous
 * user has nowhere durable to record it, and a modal that reappears whenever
 * someone clears their cache violates the annotation above.
 *
 * CURRENTLY OFF via LIVE_READINGS_PROMO_MODAL_ENABLED. The waitlist itself is
 * untouched — still reachable from the menu and at /live-readings — so this
 * component stays wired rather than deleted, and the everything-below still
 * describes how it behaves when switched back on.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import {
    APP_URL_CHAT,
    APP_URL_LIVE_READINGS,
    LIVE_READINGS_DISMISS_VIA,
    LIVE_READINGS_ENABLED,
    LIVE_READINGS_PROMO_MODAL_ENABLED
} from '../../../const';
import { getUser } from '../../../store/slices/authSlice';
import {
    clearWaitlistError,
    dismissLiveReadingsPromo,
    getWaitlistState,
    joinWaitlist,
    setShowJoinedToast
} from '../../../store/slices/waitlistSlice';
import { gaEvents } from '../../../utils/analytics';
import AvatarCluster from '../../pages/LiveReadingsPage/AvatarCluster';
import LiveReadingsValueProps from '../LiveReadingsValueProps';
import Spinner from '../Spinner';
import styles from './styles.module.scss';

const ModalCloseIcon = () => (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M18 6 6 18M6 6l12 12" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const LiveReadingsPromoModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector(getUser);

    const isUser = !!user?.id;
    const knownEmail = user?.email || '';
    const { joining, error: joinError } = useSelector(getWaitlistState);
    const { promoDismissedAt, joinedWaitlistAt } = user?.liveReadings || {};

    // Only over a conversation. The user described the moment as "when a user
    // logs in they're taken to their last conversation — show it then".
    //
    // Note this is the first chat-page landing while authenticated, NOT strictly
    // the login transition: gating on the transition alone would mean users with
    // persistent sessions (who never log out) never see it at all, which would
    // gut the demand signal. Server-side suppression keeps it once-ever either
    // way, so the looser trigger costs nothing.
    // `end: false` so this matches both `/advisor/:alias` (fresh chat) and
    // `/advisor/:alias/:chatId` (existing conversation) — a user landing on
    // either has arrived "in a conversation" for our purposes.
    const isOnChatRoute = !!matchPath({ path: `/${APP_URL_CHAT}/:alias`, end: false }, location.pathname);

    const isVisible =
        LIVE_READINGS_ENABLED && LIVE_READINGS_PROMO_MODAL_ENABLED && isUser && isOnChatRoute && !promoDismissedAt && !joinedWaitlistAt;

    useEffect(() => {
        if (!isVisible) return;
        gaEvents.liveReadingsPromoShown();
        // Drop any error left over from a failed attempt on the waitlist page
        // earlier in this session — otherwise the modal opens already showing a
        // failure the user hasn't caused yet.
        dispatch(clearWaitlistError());
    }, [isVisible, dispatch]);

    // Suppress first, then act. Firing the dismiss immediately (rather than
    // awaiting it) means a flaky network can't leave the modal re-appearing on
    // the next navigation.
    const dismiss = () => {
        gaEvents.liveReadingsPromoDismissed();
        dispatch(dismissLiveReadingsPromo(LIVE_READINGS_DISMISS_VIA.CLOSE));
    };

    // "Not Interested" suppresses exactly like the X, but is reported
    // separately. For a demand test the distinction is the point: closing a
    // modal is ambiguous (mistap, in a hurry, will look later), whereas tapping
    // an explicit decline is a deliberate "no". Collapsing both into one
    // "dismissed" number would hide the only clean negative signal this test
    // can collect.
    const handleNotInterested = () => {
        gaEvents.liveReadingsPromoDeclined();
        dispatch(dismissLiveReadingsPromo(LIVE_READINGS_DISMISS_VIA.NOT_INTERESTED));
    };

    // The modal only ever renders for a signed-in user over a conversation, so
    // in the normal case we already know the address and there is nothing to
    // ask for. Joining in place keeps them in the chat they were reading —
    // routing to a separate page to press a second button was friction with no
    // purpose, and every extra step costs conversions on the one funnel this
    // fake-door test exists to measure.
    const handleCta = async () => {
        gaEvents.liveReadingsPromoCtaClick();

        // No address on file (social login without an email scope). We have
        // nothing to submit, so fall back to the page, where they can type one.
        if (!knownEmail) {
            dispatch(dismissLiveReadingsPromo());
            navigate(`/${APP_URL_LIVE_READINGS}`, {
                state: { fromPromo: true, returnTo: `${location.pathname}${location.search}` }
            });
            return;
        }

        if (joining) return;

        gaEvents.liveReadingsWaitlistSubmit('promo_modal', true);
        const result = await dispatch(joinWaitlist(knownEmail));

        if (joinWaitlist.fulfilled.match(result)) {
            gaEvents.liveReadingsWaitlistJoined('promo_modal', true);
            // The join response stamps `liveReadings.joinedWaitlistAt` onto the
            // user, which is the same flag this modal gates on — so it closes
            // itself. No explicit dismiss needed, and none wanted: joining is
            // already permanent suppression.
            dispatch(setShowJoinedToast(true));
        }
        // On failure the modal stays open so they can retry. ResponseService
        // surfaces the error toast; silently closing would look like success.
    };

    useEffect(() => {
        if (!isVisible) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') dismiss();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    if (!isVisible) return null;

    const body = (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className={styles.overlay} onClick={dismiss}>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div
                className={styles.sheet}
                role="dialog"
                aria-modal="true"
                aria-label={t('liveReadings.headline')}
                onClick={(e) => e.stopPropagation()}
            >
                <button type="button" className={styles.close} onClick={dismiss} aria-label="Close">
                    <ModalCloseIcon />
                </button>

                <div className={styles.glow} aria-hidden="true" />

                {/*
                    Source order is the MOBILE order (cluster first, then copy).
                    Desktop turns `.sheet` into a two-column grid and places the
                    cluster in the right column, so the visual reordering is
                    done in CSS rather than by rendering the tree twice.
                */}
                <AvatarCluster className={styles.cluster} />

                <div className={styles.copy}>
                    <div className={styles.heading}>
                        <p className={styles.eyebrow}>{t('liveReadings.eyebrow')}</p>
                        <h2 className={styles.headline}>
                            {t('liveReadings.headline')} <span className={styles.headlineBrand}>{t('liveReadings.headlineBrand')}</span>
                        </h2>
                    </div>

                    <LiveReadingsValueProps />

                    <div className={styles.actions}>
                        {/*
                            Explicit inline error rather than relying on the
                            global interceptor. ResponseService only toasts for
                            400 and 500 — a 429 from the join rate limiter has
                            no listener at all, so without this the button would
                            just re-enable and nothing would happen, which reads
                            as the app ignoring the tap.
                        */}
                        {!!joinError && <span className={styles.error}>{t('liveReadings.joinError')}</span>}
                        <button type="button" className={styles.ctaButton} onClick={handleCta} disabled={joining}>
                            {joining ? <Spinner width={24} height={24} /> : t('liveReadings.joinCta')}
                        </button>
                        <button type="button" className={styles.notInterested} onClick={handleNotInterested} disabled={joining}>
                            {t('liveReadings.notInterested')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined') return body;
    return createPortal(body, document.body);
};

export default LiveReadingsPromoModal;
